import React, { Fragment, useContext, useEffect, useState } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { RootStoreContext } from "../../../app/stores/rootStore";
import { BannerFormValues } from "../../../app/models/banner";
import { Button, Form, Grid, Image, Segment } from "semantic-ui-react";
import { Form as FinalForm, Field } from "react-final-form";
import TextInput from "../../../app/common/form/TextInput";
import { combineValidators, isRequired } from "revalidate";
import { observer } from "mobx-react-lite";
import PhotoUploadWidget from "../../../app/common/photoUpload/PhotoUploadWidget";
import DropdownInput from "../../../app/common/form/DropdownInput";

const validate = combineValidators({
  code: isRequired({ message: "Código é obrigatório" }),
});

interface DetailParams {
  id: string;
}

const BannerForm: React.FC<RouteComponentProps<DetailParams>> = ({ match }) => {
  const rootStore = useContext(RootStoreContext);
  const {
    createBanner,
    editBanner,
    submitting,
    loadBanner,
    deleteBanner,
    target,
    uploadingImage,
  } = rootStore.bannerStore;

  const [banner, setBanner] = useState(new BannerFormValues());
  const [loading, setLoading] = useState(false);
  const [addImageMode, setAddImageMode] = useState(false);

  useEffect(() => {
    if (match.params.id) {
      setLoading(true);
      loadBanner(match.params.id)
        .then((banner) => setBanner(new BannerFormValues(banner)))
        .finally(() => setLoading(false));
    } else {
      setAddImageMode(true);
    }
  }, [loadBanner, match.params.id, setAddImageMode]);

  const handleUploadImage = (image: Blob) => {
    createBanner(image).then(() => setAddImageMode(false));
  };

  const handleFinalFormSubmit = (values: any) => {
    const { ...banner } = values;
    if (banner.id) {
      editBanner(banner);
    }
  };

  const bannerCodeOptions = [
    { key: "DASHBOARD", text: "Página Inicial", value: "DASHBOARD" },
    { key: "QUESTION", text: "Página de Questão", value: "QUESTION" },
    {
      key: "POPUP1QUESTION",
      text: "Popup 1 da Questão",
      value: "POPUP1QUESTION",
    },
    {
      key: "POPUP2QUESTION",
      text: "Popup 2 da Questão",
      value: "POPUP2QUESTION",
    },
  ];

  return (
    <Fragment>
      {banner.id ? <h1>Banner</h1> : <h1>Novo Banner</h1>}
      <Segment clearing>
        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              {addImageMode ? (
                <PhotoUploadWidget
                  uploadPhoto={handleUploadImage}
                  loading={uploadingImage}
                />
              ) : (
                <Fragment>
                  <Image src={banner.url} />
                  <br />
                  <FinalForm
                    validate={validate}
                    initialValues={banner}
                    onSubmit={handleFinalFormSubmit}
                    render={({ handleSubmit, invalid, pristine }) => (
                      <Form onSubmit={handleSubmit} loading={loading}>
                        <Grid>
                          <Grid.Row>
                            <Grid.Column width={4}>
                              <Field
                                component={DropdownInput}
                                options={bannerCodeOptions}
                                name="code"
                                title="Local do Banner"
                                placeholder="Selecionar Local"
                                value={banner.code}
                              />
                            </Grid.Column>
                            <Grid.Column width={12}>
                              <Field
                                name="redirectUrl"
                                title="URL de Redirecionamento"
                                placeholder="URL de Redirecionamento"
                                value={banner.redirectUrl}
                                component={TextInput}
                              />
                            </Grid.Column>
                          </Grid.Row>
                          <Grid.Row>
                            <Grid.Column width={16}>
                              <Button
                                loading={submitting}
                                disabled={loading || invalid || pristine}
                                floated="right"
                                positive
                                type="submit"
                                content="Salvar"
                              />
                              <Button
                                as={Link}
                                to="/admin/banners"
                                disabled={loading}
                                floated="right"
                                type="button"
                                content="Cancelar"
                              />
                              {banner.id && (
                                <Button
                                  loading={target === banner.id && submitting}
                                  disabled={loading}
                                  onClick={(e) => {
                                    deleteBanner(e, banner.id!);
                                    setBanner(new BannerFormValues());
                                  }}
                                  floated="right"
                                  color="red"
                                  content="Excluir"
                                />
                              )}
                            </Grid.Column>
                          </Grid.Row>
                        </Grid>
                      </Form>
                    )}
                  />
                </Fragment>
              )}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Segment>
    </Fragment>
  );
};

export default observer(BannerForm);
